import { Form } from 'components/organisms';
import { Application } from 'components/templates';
import { LabelForm } from 'pages/base';
import ErrorPage from 'pages/global/ErrorPage';
import {
  Advances,
  Artist,
  Balance,
  Certificates,
  Channel,
  ChannelVideo,
  Contract,
  Dashboard,
  Label,
  LabelReport,
  Merch,
  MusicProductDigital,
  MusicProductPhysical,
  Settings,
  Settlement,
  SpotifyStatistics,
  Track,
  Video,
} from 'pages/private';
import Calendar from 'pages/private/calendar';
import {
  Email,
  Login,
  Password,
  Register,
  RegisterConfirmation,
  ServiceLogin,
  TwoFactor,
  TwoFactorRecovery,
} from 'pages/public';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

export const routes = createBrowserRouter([
  {
    path: '/',
    errorElement: <ErrorPage />,
    element: (
      <ProtectedRoute>
        <Application />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="dashboard" />,
      },

      {
        path: 'dashboard',
        children: [
          {
            index: true,
            element: <Dashboard />,
            handle: { mainCategory: 'home' },
          },
        ],
      },
      {
        path: 'calendar',
        children: [
          {
            index: true,
            element: <Calendar.Individual />,
            handle: { mainCategory: 'home' },
          },
        ],
      },

      {
        path: 'statistics',
        children: [
          {
            index: true,
            element: <SpotifyStatistics.Individual />,
            handle: { mainCategory: 'home' },
          },
        ],
      },

      {
        path: 'artist',
        children: [
          {
            index: true,
            element: <Artist.List />,
            handle: { mainCategory: 'content' },
          },
          {
            path: ':id',
            element: <Artist.Individual />,
            children: Form.Artist.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Artist.Edit />,
            children: Form.Artist.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Artist.Create />,
            children: Form.Artist.getRouterTabs(),
          },
        ],
      },
      {
        path: 'label-report',
        children: [
          {
            index: true,
            element: <LabelReport.List />,
            handle: { mainCategory: 'finances' },
          },
        ],
      },
      {
        path: 'settlement',
        children: [
          {
            index: true,
            element: <Settlement.List />,
            handle: { mainCategory: 'finances' },
          },
        ],
      },
      {
        path: 'advance',
        children: [
          {
            index: true,
            element: <Advances.List />,
            handle: { mainCategory: 'finances' },
          },
          {
            path: ':id',
            element: <Advances.Individual />,
            children: Form.AdvanceLabelForm.getRouterTabs(),
          },
        ],
      },
      {
        path: 'balance',
        children: [
          {
            index: true,
            element: <Balance.List />,
            handle: { mainCategory: 'finances' },
          },

          {
            path: 'income',
            children: [
              {
                path: ':id',
                element: <Balance.IndividualIncom />,
                children: Form.CashFlow.getRouterTabs(),
              },
            ],
          },
          {
            path: 'cost',
            children: [
              {
                path: ':id',
                element: <Balance.IndividualCost />,
                children: Form.CashFlow.getRouterTabs(),
              },
            ],
          },
        ],
      },
      {
        path: 'track',
        children: [
          {
            index: true,
            element: <Track.List />,
            handle: { mainCategory: 'content' },
          },
          {
            path: ':id',
            element: <Track.Individual />,
            children: Form.Track.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Track.Edit />,
            children: Form.Track.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Track.Create />,
            children: Form.Track.getRouterTabs(),
          },
        ],
      },
      {
        path: 'video',
        children: [
          {
            index: true,
            element: <Video.List />,
            handle: { mainCategory: 'content' },
          },
          {
            path: ':id',
            element: <Video.Individual />,
            children: Form.Video.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Video.Edit />,
            children: Form.Video.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Video.Create />,
            children: Form.Video.getRouterTabs(),
          },
        ],
      },
      {
        path: 'music-product-digital',
        children: [
          {
            index: true,
            element: <MusicProductDigital.List />,
            handle: { mainCategory: 'products' },
          },
          {
            path: ':id',
            element: <MusicProductDigital.Individual />,
            children: Form.MusicProductDigital.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <MusicProductDigital.Edit />,
            children: Form.MusicProductDigital.getRouterTabs(),
          },
          {
            path: 'create',
            element: <MusicProductDigital.Create />,
            children: Form.MusicProductDigital.getRouterTabs(),
          },
        ],
      },
      {
        path: 'music-product-physical',
        children: [
          {
            index: true,
            element: <MusicProductPhysical.List />,
            handle: { mainCategory: 'products' },
          },
          {
            path: ':id',
            element: <MusicProductPhysical.Individual />,
            children: Form.MusicProductPhysical.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <MusicProductPhysical.Edit />,
            children: Form.MusicProductPhysical.getRouterTabs(),
          },
          {
            path: 'create',
            element: <MusicProductPhysical.Create />,
            children: Form.MusicProductPhysical.getRouterTabs(),
          },
        ],
      },
      {
        path: 'merch',
        children: [
          {
            index: true,
            element: <Merch.List />,
            handle: { mainCategory: 'products' },
          },
          {
            path: ':id',
            element: <Merch.Individual />,
            children: Form.Merch.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Merch.Edit />,
            children: Form.Merch.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Merch.Create />,
            children: Form.Merch.getRouterTabs(),
          },
        ],
      },
      {
        path: 'channel',
        children: [
          {
            index: true,
            element: <Channel.List />,
            handle: { mainCategory: 'products' },
          },
          {
            path: ':id',
            element: <Channel.Individual />,
            children: Form.Channel.getRouterTabs(),
          },
          {
            path: ':id/channel-video/:videoId',
            element: <ChannelVideo.Individual />,
            children: Form.ChannelVideo.getRouterTabs(),
          },
        ],
      },
      {
        path: 'contracts',
        children: [
          {
            index: true,
            element: <Contract.List />,
            handle: { mainCategory: 'finances' },
          },

          {
            path: ':id',
            element: <Contract.Individual />,
            children: Form.Contract.getRouterTabs(),
          },
        ],
      },

      {
        path: 'label',
        element: <Label.Individual />,
      },

      {
        path: 'certificates',
        element: <Certificates.List />,
      },

      { path: 'preferences', element: <Settings.User /> },
      { path: 'security', element: <Settings.Security /> },
      {
        path: 'support',
        children: [
          { index: true, element: <Settings.Support.Threads /> },
          { path: 'create', element: <Settings.Support.Create /> },
          { path: ':id', element: <Settings.Support.Chat /> },
        ],
      },
    ],
  },
  {
    element: (
      <ProtectedRoute>
        <LabelForm />
      </ProtectedRoute>
    ),
    path: 'label-form',
  },
  {
    element: <PublicRoute />,
    children: [
      { path: '*', element: <Navigate to="/login" /> },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'register',

        children: [
          { index: true, element: <Register /> },
          { path: 'confirm', element: <RegisterConfirmation /> },
        ],
      },
      {
        path: 'reset-password',
        children: [
          {
            index: true,
            element: <Password.Reset />,
          },
          {
            path: 'request',
            element: <Password.Request />,
          },
          {
            path: 'resend',
            element: <Password.RequestAgain />,
          },
          {
            path: 'confirmation',
            element: <Password.Confirmation />,
          },
        ],
      },
      {
        path: 'confirm-email',
        children: [
          {
            index: true,
            element: <Email.Source />,
          },
          {
            path: 'confirm',
            element: <Email.Confirm />,
          },
          {
            path: 'setup-password',
            element: <Email.SetupPassword />,
          },
          {
            path: 'resend',

            element: <Email.ResendConfirm />,
          },
        ],
      },

      {
        path: 'two-factor',
        children: [
          { index: true, element: <TwoFactor /> },
          {
            path: ':token/:email',
            children: [
              { index: true, element: <TwoFactor /> }, //when user pass code from email
              { path: ':code', element: <TwoFactor /> }, //when user click on email link
            ],
          },
        ],
      },
      {
        path: 'service-code/:token',
        element: <ServiceLogin />,
      },
      {
        path: 'two-factor-recovery',
        element: <TwoFactorRecovery />,
      },
    ],
  },
]);
